import { useQuery } from '@tanstack/react-query';
import { api, type ProjectSummary } from '../api/client';

interface Props {
  onOpen: (projectId: string) => void;
  onCreate: () => void;
}

export function ProjectsScreen({ onOpen, onCreate }: Props) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: api.listProjects,
    refetchInterval: 4000,
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">My sites</h1>
        <button
          type="button"
          onClick={onCreate}
          className="bg-brand text-white px-4 py-2 rounded font-medium hover:bg-brand-dark transition-colors"
        >
          + Create site
        </button>
      </div>

      {isLoading && <div className="text-gray-500">Loading...</div>}
      {error && <div className="text-red-600 bg-red-50 border border-red-200 p-3 rounded">Error: {String(error)}</div>}

      {data && data.projects.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sites yet</h3>
          <p className="text-sm text-gray-500 mb-6">
            Create your first site by pasting your brand JSON. The engine handles the rest.
          </p>
          <button
            type="button"
            onClick={onCreate}
            className="bg-brand text-white px-5 py-2.5 rounded font-medium hover:bg-brand-dark transition-colors"
          >
            + Create your first site
          </button>
        </div>
      )}

      {data && data.projects.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left p-3 text-xs font-medium text-gray-700 uppercase tracking-wider">Brand</th>
                <th className="text-left p-3 text-xs font-medium text-gray-700 uppercase tracking-wider">Kit</th>
                <th className="text-left p-3 text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="text-left p-3 text-xs font-medium text-gray-700 uppercase tracking-wider">Updated</th>
                <th className="text-right p-3 text-xs font-medium text-gray-700 uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.projects.map((p: ProjectSummary) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="p-3 font-medium">{p.brandId}</td>
                  <td className="p-3 text-sm text-gray-700">{p.kitId}</td>
                  <td className="p-3">
                    <StatusPill projectId={p.id} />
                  </td>
                  <td className="p-3 text-sm text-gray-500">
                    {new Date(p.updatedAt).toLocaleString()}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => onOpen(p.id)}
                      className="text-brand hover:text-brand-dark text-sm font-medium"
                    >
                      Open →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
}

function StatusPill({ projectId }: { projectId: string }) {
  const { data } = useQuery({
    queryKey: ['projectState', projectId],
    queryFn: () => api.getProjectState(projectId),
    refetchInterval: 4000,
  });
  if (!data) return <span className="text-xs text-gray-400">…</span>;
  const status = data.buildRun?.status ?? 'idle';
  const colors: Record<string, string> = {
    idle: 'bg-gray-100 text-gray-700',
    queued: 'bg-yellow-100 text-yellow-800',
    running: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? colors.idle}`}>
      {status}
      {data.buildRun?.lastCompletedPhase && status === 'running' && (
        <span className="opacity-75"> · {data.buildRun.lastCompletedPhase}</span>
      )}
    </span>
  );
}

